import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/services/adminApi";
import {
  RefreshCcw,
  AlertTriangle,
  Clock,
  Activity,
  Trash2,
  ChevronDown,
  ChevronRight,
  Filter,
} from "lucide-react";

interface OperationLog {
  id: string;
  operationType: string;
  status: string;
  totalDurationMs: number;
  isSlow: boolean;
  chatId: string | null;
  messageId: string | null;
  userId: string | null;
  organizationId: string | null;
  metadata: any;
  createdAt: string;
}

interface Stats {
  totalCount: number;
  slowCount: number;
  avgDurationMs: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function durationColor(ms: number): string {
  if (ms < 2000) return "text-green-600";
  if (ms < 5000) return "text-yellow-600";
  return "text-red-600";
}

function statusBadge(status: string) {
  switch (status) {
    case "success":
      return <Badge className="bg-green-100 text-green-800">success</Badge>;
    case "error":
      return <Badge className="bg-red-100 text-red-800">error</Badge>;
    case "timeout":
      return <Badge className="bg-orange-100 text-orange-800">timeout</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
  }
}

function MetadataBreakdown({ metadata }: { metadata: any }) {
  if (!metadata) return <p className="text-sm text-gray-500">No metadata</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
      {metadata.goService && (
        <div className="space-y-1">
          <h4 className="font-semibold text-gray-700">Go Service</h4>
          <div className="bg-gray-50 rounded p-2 space-y-0.5 font-mono text-xs">
            {Object.entries(metadata.goService).map(([key, val]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-500">{key}</span>
                <span className={typeof val === "number" && val > 2000 ? "text-red-600 font-bold" : ""}>
                  {typeof val === "number" ? formatDuration(val as number) : String(val)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {(metadata.bridge || metadata.cacheBridge) && (
        <div className="space-y-1">
          <h4 className="font-semibold text-gray-700">Bridge</h4>
          <div className="bg-gray-50 rounded p-2 space-y-0.5 font-mono text-xs">
            {Object.entries(metadata.bridge || metadata.cacheBridge).map(([key, val]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-500">{key}</span>
                <span>
                  {typeof val === "number" ? formatDuration(val as number) : String(val)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {metadata.agentLoop && (
        <div className="space-y-1">
          <h4 className="font-semibold text-gray-700">Agent Loop</h4>
          <div className="bg-gray-50 rounded p-2 space-y-0.5 font-mono text-xs">
            {Object.entries(metadata.agentLoop).map(([key, val]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-500">{key}</span>
                <span>
                  {typeof val === "number" ? formatDuration(val as number) : String(val ?? "n/a")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {metadata.models && (
        <div className="space-y-1">
          <h4 className="font-semibold text-gray-700">Models</h4>
          <div className="bg-gray-50 rounded p-2 space-y-0.5 font-mono text-xs">
            {Object.entries(metadata.models).map(([key, val]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-500">{key}</span>
                <span>{String(val ?? "n/a")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {metadata.selectedModel && (
        <div className="space-y-1">
          <h4 className="font-semibold text-gray-700">Selected Model</h4>
          <div className="bg-gray-50 rounded p-2 space-y-0.5 font-mono text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">provider</span>
              <span>{metadata.selectedProvider}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">model</span>
              <span>{metadata.selectedModel}</span>
            </div>
          </div>
        </div>
      )}
      {metadata.error && (
        <div className="col-span-full">
          <h4 className="font-semibold text-red-700">Error</h4>
          <p className="text-red-600 text-xs font-mono bg-red-50 rounded p-2">
            {metadata.error}
          </p>
        </div>
      )}
      {metadata.query && (
        <div className="col-span-full">
          <h4 className="font-semibold text-gray-700">Query</h4>
          <p className="text-gray-600 text-xs bg-gray-50 rounded p-2 truncate">
            {metadata.query}
          </p>
        </div>
      )}
    </div>
  );
}

export default function OperationLogsPage() {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Filters
  const [operationType, setOperationType] = useState<string>("");
  const [slowOnly, setSlowOnly] = useState(false);
  const [chatIdFilter, setChatIdFilter] = useState("");

  // Purge
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeDays, setPurgeDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        adminApi.getOperationLogs({
          operationType: operationType || undefined,
          isSlow: slowOnly ? true : undefined,
          chatId: chatIdFilter || undefined,
          page,
          limit: 30,
        }),
        adminApi.getOperationLogStats({
          operationType: operationType || undefined,
        }),
      ]);
      setLogs(logsRes.logs);
      setTotalPages(logsRes.totalPages);
      setTotal(logsRes.total);
      setStats(statsRes);
    } catch (err) {
      console.error("Failed to fetch operation logs:", err);
    } finally {
      setLoading(false);
    }
  }, [page, operationType, slowOnly, chatIdFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePurge = async () => {
    try {
      const result = await adminApi.purgeOperationLogs(purgeDays);
      setPurgeConfirm(false);
      alert(`Purged ${result.deletedCount} logs older than ${purgeDays} days`);
      fetchData();
    } catch (err) {
      console.error("Purge failed:", err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Operation Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Performance observability for context resolution and other operations
          </p>
        </div>
        <div className="flex gap-2">
          {!purgeConfirm ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPurgeConfirm(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" /> Purge
            </Button>
          ) : (
            <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-1.5">
              <span className="text-sm text-red-700">Delete older than</span>
              <input
                type="number"
                value={purgeDays}
                onChange={(e) => setPurgeDays(Number(e.target.value))}
                className="w-16 text-sm border rounded px-2 py-1"
                min={1}
              />
              <span className="text-sm text-red-700">days</span>
              <Button size="sm" variant="destructive" onClick={handlePurge}>
                Confirm
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPurgeConfirm(false)}
              >
                Cancel
              </Button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500">
                Total Ops
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-red-500" /> Slow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.slowCount}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500">
                Avg
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatDuration(stats.avgDurationMs)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500">
                p50
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatDuration(stats.p50Ms)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500">
                p95
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${durationColor(stats.p95Ms)}`}>
                {formatDuration(stats.p95Ms)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500">
                p99
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${durationColor(stats.p99Ms)}`}>
                {formatDuration(stats.p99Ms)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={operationType}
            onChange={(e) => {
              setOperationType(e.target.value);
              setPage(1);
            }}
            className="text-sm border rounded px-2 py-1.5"
          >
            <option value="">All types</option>
            <option value="context_resolve">context_resolve</option>
            <option value="model_select">model_select</option>
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={slowOnly}
            onChange={(e) => {
              setSlowOnly(e.target.checked);
              setPage(1);
            }}
          />
          Slow only
        </label>
        <input
          type="text"
          placeholder="Filter by chatId..."
          value={chatIdFilter}
          onChange={(e) => {
            setChatIdFilter(e.target.value);
            setPage(1);
          }}
          className="text-sm border rounded px-2 py-1.5 w-64"
        />
        <span className="text-sm text-gray-400">{total} results</span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Chat ID</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Slow</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <RefreshCcw className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-8 text-gray-400"
                  >
                    No operation logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <>
                    <TableRow
                      key={log.id}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        log.isSlow ? "bg-red-50/50" : ""
                      }`}
                      onClick={() =>
                        setExpandedRow(expandedRow === log.id ? null : log.id)
                      }
                    >
                      <TableCell>
                        {expandedRow === log.id ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge className="font-mono text-xs border border-gray-300 bg-transparent">
                          {log.operationType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`font-mono font-bold ${durationColor(
                            log.totalDurationMs
                          )}`}
                        >
                          {formatDuration(log.totalDurationMs)}
                        </span>
                      </TableCell>
                      <TableCell>{statusBadge(log.status)}</TableCell>
                      <TableCell
                        className="text-xs font-mono text-gray-500 max-w-[120px] truncate cursor-pointer hover:text-blue-600"
                        title={log.chatId || undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (log.chatId) {
                            navigator.clipboard.writeText(log.chatId);
                          }
                        }}
                      >
                        {log.chatId || "-"}
                      </TableCell>
                      <TableCell
                        className="text-xs font-mono text-gray-500 max-w-[120px] truncate cursor-pointer hover:text-blue-600"
                        title={log.userId || undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (log.userId) {
                            navigator.clipboard.writeText(log.userId);
                          }
                        }}
                      >
                        {log.userId || "-"}
                      </TableCell>
                      <TableCell>
                        {log.isSlow && (
                          <Badge className="bg-red-100 text-red-800">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            slow
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedRow === log.id && (
                      <TableRow key={`${log.id}-detail`}>
                        <TableCell colSpan={8} className="bg-gray-50/80 p-4">
                          <div className="flex gap-4 mb-3 text-xs font-mono text-gray-500">
                            {log.chatId && (
                              <span>
                                <span className="text-gray-400">chatId: </span>
                                <span
                                  className="cursor-pointer hover:text-blue-600"
                                  onClick={() => navigator.clipboard.writeText(log.chatId!)}
                                  title="Click to copy"
                                >
                                  {log.chatId}
                                </span>
                              </span>
                            )}
                            {log.messageId && (
                              <span>
                                <span className="text-gray-400">messageId: </span>
                                <span
                                  className="cursor-pointer hover:text-blue-600"
                                  onClick={() => navigator.clipboard.writeText(log.messageId!)}
                                  title="Click to copy"
                                >
                                  {log.messageId}
                                </span>
                              </span>
                            )}
                            {log.organizationId && (
                              <span>
                                <span className="text-gray-400">orgId: </span>
                                <span
                                  className="cursor-pointer hover:text-blue-600"
                                  onClick={() => navigator.clipboard.writeText(log.organizationId!)}
                                  title="Click to copy"
                                >
                                  {log.organizationId}
                                </span>
                              </span>
                            )}
                          </div>
                          <MetadataBreakdown metadata={log.metadata} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
